/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import HomeV2Status, { getAliProductStatus } from '@/api/homeV2Status';
import store from '@/store';
import RootStateType from '@/store/types';
import generateMutations, { UPDATE_STATE_TYPE } from '@/store/generate-mutations';
import { ActionContext, Module } from 'vuex';

import type {
  AcceptTaskListParamsType,
  GetRewardingTaskListParams,
  ReceiveAwardParamsType,
  RewardingTasksInfo,
  TaskType,
} from '@/api/rewarding/types';
import { galileoTypeEnum, UpgradeStateEnum } from '@/enums/autarkyUpgrade';
import { appEnv } from '@/components/adv-components';

import { acceptRewardingTaskList, getRewardingTaskList, receiveAward } from '@/api/rewarding';
import BillAPI from '@/api/bill';
import { local, session } from '@/shared/utils/storage';
import { userActionTypeEnum } from '@/api/callBack/types';
import CallBackAPI from '@/api/callBack';
import { getGalileoAb } from '@/shared/galileoAb';
import { ubtSendError, ubtModuleExposeNew } from '@/shared/utils/ubt';
import { advMasterApi } from '@hb/h5-components';
import platform from '@/shared/utils/platform';
import { getSkPreRequestData, skNativeContainer } from '@/api/native/container';
import { UserCreditType } from '@/api/homeV2Status/types';
import { dialogHelper } from '@/shared/utils/dialogHelper';
import { UserHomeSceneEnums } from '@/enums/home-type';
import { dispatchActions } from '@/store/dispatchActions';
import { homeStoreActions } from '@/pages/home/store';
import {
  CreditBlockedEnums, FinanceCardAnalyzeAdvData, StateType,
} from './types';
import { AdvCoreData, analyzeFinanceCardAdvData, TAGLESS_FINANCE_CARD_ADV_ID } from './const';

const moduleStore: Module<StateType, RootStateType> = {
  namespaced: true,
  state: {
    notice: undefined,
    creditData: null,
    shouldAnimate: true,
    categoryMaxCount: 5,
    categoryLineCount: 4,
    aliProductStatus: {
      dialogText: '',
      buttonText: '',
    },
    progress: '',
    canApplyCredit: true,
    canSlidInQuality: true,
    autarkyState: undefined,
    marketHeight: '',
    marketSource: '',
    marketHasLoaded: false,
    /** 首页主题类型（有缓存） */
    homeThemeType: null,
    homeLoadAdvRefreshSignal: 0,
    advHomeFloatHasVisible: false,
    loopQueryPreCreditIssued: false,
  },
  getters: {
    /** 是否老客升级 实验组1
     * 品质首页，老客升级模块 1. 需要隐藏去提现模块 2.需要把 立即升级 修改成 去提现
     * 具体样式变动参考需求分析第3点：https://hellobike.yuque.com/yxr77w/oyyz8n/kv2bigpd7t78v4ca#qBd4M
    */
    isExperimentA(state: StateType) {
      const { autarkyState } = state;
      return autarkyState && autarkyState.autarkyUpgradeState === UpgradeStateEnum.INIT
      && autarkyState.galileoType === galileoTypeEnum.EXPERIMENT_A;
    },
    /**
     * 授用信一体化需求（https://metis2.hellobike.cn/browse/SKXDZC-5851）中
     * 针对命中老客升级条件用户，实验组1、2 用户，在提现协议页，展示 【升级新产品】入口，勾选展示自营 B 8180授信协议。
     * 具体样式变动参考需求分析第4点：https://hellobike.yuque.com/yxr77w/oyyz8n/kv2bigpd7t78v4ca#qBd4M
     */
    isExperimentAB(state: StateType) {
      const { autarkyState } = state;
      return autarkyState && autarkyState.autarkyUpgradeState === UpgradeStateEnum.INIT
      && (
        autarkyState.galileoType === galileoTypeEnum.EXPERIMENT_A
        || autarkyState.galileoType === galileoTypeEnum.EXPERIMENT_B
      );
    },
  },
  actions: {
    // 获取用户信息
    async getCreditData(
      context: ActionContext<StateType, RootStateType>,
    ) {
      try {
        // 是否已经刷新过用户额度以及还款计划数据, 默认每一次从APP进入首页需要刷新用户额度以及还款计划数据
        const hasRefreshCredit = session.get('hasRefreshCredit');
        if (!hasRefreshCredit) {
          BillAPI.refreshCreditAndRepayPlan().then((result) => {
            if (result && result.isCreditUpdated) {
              context.dispatch('getCreditData');
            }
          });

          session.set('hasRefreshCredit', true);
          // 新增 用户运营事件采集 -- 首页访问
          const params = {
            userAction: userActionTypeEnum.IndexVisit,
          };
          CallBackAPI.userActionCallBack(params);
        }

        /** 获取客户端传递 授信相关数据 */
        const nativeCreditData = await getSkPreRequestData({
          project: 'AppFinanceH5Web',
          action: 'f.loan.panel.queryUserCredit',
        });

        // 是否从客户端容器化中获取到预请求数据
        const hasNativeData = Object.keys(nativeCreditData).length > 0;

        // 获取授信数据：通过客户端容器化方法获取数据，若无数据则请求接口兜底
        const getUserCredit = () => {
          const res = hasNativeData
            ? Promise.resolve(nativeCreditData) : dispatchActions.userCreditStatus.getUserCredit();
          res.then(() => {
            // 埋点 获取授信数据获取成功
            ubtModuleExposeNew('finance_home', 'finance_loan_home_creditData_success', {
              afterNavigatorStartTime: Date.now() - window.performance.timing.navigationStart,
              link: window.location.href,
            });
          });
          return res;
        };

        // 获取用户金融信息, 通过3个接口拼接
        const creditCombineData = await Promise.all([
          getUserCredit(),
          HomeV2Status.getUserLoan(),
          dispatchActions.userProfileProgress.getUserInfoProgress(),
        ]);

        // 埋点 首页所有业务数据获取成功
        ubtModuleExposeNew('finance_home', 'finance_loan_home_allBusinessData_success', {
          afterNavigatorStartTime: Date.now() - window.performance.timing.navigationStart,
          link: window.location.href,
        });

        // 判断在容器化中，预请求获取 成功/失败 的埋点
        skNativeContainer().then((isNativeContainer) => {
          if (isNativeContainer) {
            const nativeDataResEventId = hasNativeData ? 'finance_container_tabbar_pre_success' : 'finance_container_tabbar_pre_fail';
            ubtModuleExposeNew('finance_container_tabbar', nativeDataResEventId);
          }
        });

        /** 数据拼接 */
        const creditData = {
          ...(creditCombineData[0] as UserCreditType).businessData,
          ...creditCombineData[1],
          ...creditCombineData[2],
        };

        const hasOverdue = creditData && creditData.overdueNum && Number(creditData.overdueNum);
        context.dispatch(UPDATE_STATE_TYPE, {
          creditData,
          notice: hasOverdue ? `您有 ${creditData.overdueNum} 笔提现已逾期，请尽快还款` : '',
        });

        return Promise.resolve();
      } catch (e) {
        return Promise.resolve();
      }
    },

    // 查询奖励金任务列表
    async getRewardingTaskList(
      context: ActionContext<StateType, RootStateType>,
      params: GetRewardingTaskListParams,
    ): Promise<RewardingTasksInfo | undefined> {
      try {
        const data = await getRewardingTaskList(params);

        return Promise.resolve(data);
      } catch (error) {
        return Promise.resolve(undefined);
      }
    },

    // 查询奖励金任务列表
    async acceptRewardingTaskList(
      context: ActionContext<StateType, RootStateType>,
      params: AcceptTaskListParamsType,
    ): Promise<TaskType[]> {
      try {
        const data = await acceptRewardingTaskList(params);

        return Promise.resolve(data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // 手动领取奖励金活动奖励
    async receiveAward(
      context: ActionContext<StateType, RootStateType>,
      params: ReceiveAwardParamsType,
    ): Promise<boolean> {
      try {
        const data = await receiveAward(params);

        return Promise.resolve(!!data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // 获取支付宝小程序产品状态
    async getAliProductStatus(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      try {
        const aliProductStatus = await getAliProductStatus();
        // 弹窗提示主文案
        let dialogText = '';
        // 首页按钮提示文案
        let buttonText = '';
        // 授信状态
        let status = '';

        // 放款恢复年份
        let resumeYear = '';
        // 今天
        const now = new Date();
        // 今年年份
        const nowYear = now.getFullYear().toString();
        // 放款恢复年份文案
        let yearText = '';
        // 放款恢复月份文案
        let monthText = '';
        // 放款恢复天文案
        let dayText = '';
        // 放款恢复时间文案
        let timeText = '';

        if (aliProductStatus.resumeTime) {
          const resumeTime = new Date(aliProductStatus.resumeTime);
          resumeYear = resumeTime.getFullYear().toString();
          yearText = resumeYear === nowYear ? '' : `${resumeYear}年`;
          monthText = `${resumeTime.getMonth() + 1}月`;
          dayText = `${resumeTime.getDate()}日`;
          // 判断启动时间是否是明天
          const isTomorrow = new Date(resumeTime.toJSON().substring(0, 10)).getTime()
            - new Date(now.toJSON().substring(0, 10)).getTime() === 24 * 60 * 60 * 1000;
          // 是否是整点
          const isNoMinutes = !(resumeTime.getMinutes());
          const resumeHours = resumeTime.getHours();
          const resumeTimes = aliProductStatus.resumeTime.substring(11, 16);
          if (isTomorrow) {
            timeText = `明日${resumeHours < 12 ? '早' : ''}${isNoMinutes ? `${resumeHours}点` : `${resumeTimes}`}`;
          } else {
            timeText = `${yearText}${monthText}${dayText}${resumeTimes}`;
          }
        }

        if (aliProductStatus.isStop) {
          dialogText = '系统升级中，额度审批暂停，造成不便敬请谅解';
          buttonText = '系统升级中，请稍后再试';
          status = CreditBlockedEnums.UPGRADE;
        } else {
          if (aliProductStatus.isSuspend) {
            if (aliProductStatus.isLimitToday) {
              dialogText = '抱歉，今日申请已满，请于明日早8点后重新尝试';
              buttonText = '今日申请已满，请明日再来';
              status = CreditBlockedEnums.FULL;
            } else {
              if (timeText) {
                dialogText = `系统升级中，额度审批暂停，请于${timeText}后重新尝试`;
              } else {
                dialogText = '系统升级中，额度审批暂停，造成不便敬请谅解';
              }
              buttonText = '系统升级中，请稍后再试';
              status = CreditBlockedEnums.SUSPEND;
            }
          }
        }

        context.dispatch(UPDATE_STATE_TYPE, {
          aliProductStatus: {
            dialogText,
            buttonText,
            status,
            ...aliProductStatus,
          },
        });
      } catch (error) {
        // 静默处理
      }
    },

    // 获取自营用户状态
    async getUserAutarkyState(
      context: ActionContext<StateType, RootStateType>,
    ) {
      try {
        const autarkyState = await dispatchActions.userAutarkyStatus.getUserAutarkyState();
        const {
          autarky, autarkyUpgradeState, galileoType, canApplyCredit,
        } = autarkyState || {};
        context.dispatch(UPDATE_STATE_TYPE, {
          canApplyCredit,
          autarkyState: {
            autarky,
            autarkyUpgradeState,
            galileoType,
          },
        });

        return Promise.resolve();
      } catch (e) {
        return Promise.resolve();
      }
    },

    // 查询是否可以通过上滑的方式由无标签首页进入品质版首页的ab测结果
    async getCanSlideInQualityAb(
      context: ActionContext<StateType, RootStateType>,
    ) {
      try {
        const { creditStatus = false, preCredit } = context.state.creditData || {};

        // ab测目标用户是未授信用户
        if (!creditStatus) {
          const res = await getGalileoAb().getAbRes('202306281945331919', '202306281958344438');
          context.dispatch(UPDATE_STATE_TYPE, {
            canSlidInQuality: res.result !== '0',
          });
        }

        /** 预授信用户，不能上滑 */
        if (preCredit) {
          context.dispatch(UPDATE_STATE_TYPE, {
            canSlidInQuality: false,
          });
        }

        return Promise.resolve();
      } catch (e) {
        return Promise.resolve();
      }
    },

    /** 初始化首页广告位数据，仅哈啰app环境展示广告位（目前仅有-无标签首页的广告） */
    async getFianceCardAdvConfig(
      context: ActionContext<StateType, RootStateType>,
    ) {
      /** 仅哈啰app的环境下，才展示广告位 */
      if (!platform.isEasyBike) return Promise.resolve();

      try {
        /** 广告位缓存数据，以用户维度隔离开 */
        const marketLocalDataKey = `fianceCardAdvConfig_${store.state.base.userId}`;
        const cacheData: FinanceCardAnalyzeAdvData | null = local.get(marketLocalDataKey);
        /** 当前是否使用本地缓存 */
        let currentUsedCache = false;
        /** 针对Api返回广告位数据进行处理(提取有效数据) */
        const handleApiAdvData = <T extends AdvCoreData>(advData: T) => {
          const result = analyzeFinanceCardAdvData<T>(advData);

          if (result && result.marketSource && result.marketHeight) {
            local.set(marketLocalDataKey, result);
            const { marketHasLoaded } = context.state;

            /**
               * 当广告接口请求数据已返回后，除了更新到本地缓存外
               * 若页面还未渲染广告位，则立即更新store，渲染广告位
               * 若页面已渲染广告位(来自缓存)，则用户下次进来再从缓存中读取新的广告位数据(运营更新广告位的频率，极低)
               */
            if (!marketHasLoaded) {
              context.dispatch(UPDATE_STATE_TYPE, {
                marketSource: result.marketSource,
                marketHeight: result.marketHeight,
                marketHasLoaded: true,
              });
            }
          } else {
            /** 此时广告位无数据，说明广告已下线、过期，则删除本地缓存 */
            local.remove(marketLocalDataKey);
          }
        };
        /**
         * 若已存在广告位的数据缓存，则优先设置到store，进行渲染展示
         */
        if (cacheData && cacheData.marketSource && cacheData.marketHeight) {
          context.dispatch(UPDATE_STATE_TYPE, {
            marketSource: cacheData.marketSource,
            marketHeight: cacheData.marketHeight,
            marketHasLoaded: true,
          });
          currentUsedCache = true;
        }

        if (currentUsedCache) {
          /** 有本地缓存的情况下，静默请求广告数据，在后台更新缓存，不阻塞主流程 */
          advMasterApi.getBannerInfos(
            TAGLESS_FINANCE_CARD_ADV_ID,
            appEnv,
            {},
          ).then(handleApiAdvData);
        } else {
          /** 无缓存的情况下，同步请求广告数据，等待数据返回后，再展示页面的金融卡片 */
          const advData = await advMasterApi.getBannerInfos(
            TAGLESS_FINANCE_CARD_ADV_ID,
            appEnv,
            {},
          );

          handleApiAdvData(advData);
        }
      } catch (err) {
        ubtSendError(err as Error);
      }

      return Promise.resolve();
    },

    /** 刷新首页数据: 开屏广告位 && 授信额度 */
    refreshHomePageByIssuedTempAmount(
      context: ActionContext<StateType, RootStateType>,
    ) {
      dialogHelper.applyOpenDialog(false, () => {
        /** 刷新首页广告位 */
        context.dispatch(UPDATE_STATE_TYPE, {
          homeLoadAdvRefreshSignal: Date.now(),
        });
        /** 刷新用户授信额度 */
        context.dispatch('getCreditData');
      });
    },
    /** 刷新首页用户额度相关数据 */
    async refreshHomePageByUserCredit(
      context: ActionContext<StateType, RootStateType>,
    ) {
      const { creditData } = context.state;
      /** 自营升级老客，有可能分发预授信资格，所以需要刷新老客状态 */
      const [{ businessData: userCreditData }, autarkyState] = await Promise.all([
        HomeV2Status.getUserCredit(),
        HomeV2Status.getUserAutarkyState(),
      ]);

      if (userCreditData) {
        const {
          autarky, autarkyUpgradeState, galileoType, canApplyCredit,
        } = autarkyState || {};
        const wantUpdateState = {
          loopQueryPreCreditIssued: true,
          creditData: {
            ...creditData,
            ...userCreditData,
          },
          /** 更新老客状态，若是老客升级用户命中预授信，则需切换至“无标签”首页展示 */
          canApplyCredit,
          autarkyState: { autarky, autarkyUpgradeState, galileoType },
        };
        /**
         * 若授信额度有更新，则展示“额度更新提示”弹窗
         * 若当前已展示其他弹窗，则本次不再展示弹窗
         */
        if (creditData?.creditAmount !== userCreditData.creditAmount) {
          const canShowDialog = dialogHelper.optionalOpenDialog(() => {
            homeStoreActions.setCreditAmountAlterPopupVisible(true, userCreditData.creditAmount);
            /** 延迟刷新金融卡片额度数据，保证弹窗展示ready(弹窗打开transition-duration：默认200ms)，再刷新数据 */
            setTimeout(() => {
              context.dispatch(UPDATE_STATE_TYPE, wantUpdateState);
            }, 300);
          });

          if (!canShowDialog) {
            /** 刷新金融卡片额度数据 */
            context.dispatch(UPDATE_STATE_TYPE, wantUpdateState);
          }
        }

        const {
          preCredit, preCreditUserSource, normalPreCreditModel,
        } = userCreditData;

        /** 手动刷新-预授信模块曝光埋点(现有card因为只是修改额度，所以不会自动曝光) */
        ubtModuleExposeNew('finance_taglessHome', 'finance_loan_home_financeCard', {
          scene: preCredit
            ? UserHomeSceneEnums.PRE_CREDIT : UserHomeSceneEnums.TAGLESS,
          preCreditUserSource,
          normalPreCreditModel,
        });
      }
    },
    /** 同步设置用户授信数据 */
    updateCreditData(
      context: ActionContext<StateType, RootStateType>,
      payload: {
        creditData: StateType['creditData'],
      },
    ) {
      const { creditData } = payload;
      const hasOverdue = creditData && creditData.overdueNum && Number(creditData.overdueNum);

      context.dispatch(UPDATE_STATE_TYPE, {
        creditData,
        notice: hasOverdue ? `您有 ${creditData.overdueNum} 笔提现已逾期，请尽快还款` : '',
      });
    },
  },
};

store.registerModule(
  'home',
  generateMutations<StateType>(moduleStore),
  {
    preserveState: !!store.state.home,
  },
);
